import { Children, memo, ReactNode, useEffect, useId, useState } from "react";
import classNames from "classnames";
import { TabHeaderDescription, useTabsContext } from "../TabsContext";
import { TabStrategyType } from "../types";

import styles from "./style.module.scss";

export type TabProps = Omit<TabHeaderDescription, "children"> & {
  header: ReactNode,
  children?: ReactNode,
  tabContentClassName?: string,
  strategy?: TabStrategyType
}

export const Tab = memo<TabProps>((
  {
    header,
    children,
    tabContentClassName,
    strategy,
    ...tabHeaderProps
  }) => {
  const { activeTabId, registerTab, unregisterTab, strategy: tabsStrategy } = useTabsContext();
  const [ wasActive, setWasActive ] = useState(false);
  const reactId = useId();
  const realId = tabHeaderProps.id ?? reactId;
  const isActiveTab = activeTabId === realId;
  const activeStrategy = strategy ?? tabsStrategy;

  useEffect(() => {
    registerTab(realId, {
      ...tabHeaderProps,
      id: realId,
      children: header,
    });
    return () => unregisterTab(realId);
  }, [ registerTab, realId, unregisterTab, header, tabHeaderProps ]);

  useEffect(() => {
    if (isActiveTab) {
      setWasActive(true);
    }
  }, [ isActiveTab ]);

  if (Children.count(children) === 0) {
    return null;
  }

  return (
    <>
      {
        activeStrategy === "hide" &&
        <div className={classNames(tabContentClassName, styles.content, { [styles.invisibleTab]: !isActiveTab })}>
          {children}
        </div>
      }
      {
        (activeStrategy === "collapse" && isActiveTab) &&
        <div className={classNames(tabContentClassName, styles.content)}>
          {children}
        </div>
      }
      {
        activeStrategy === "once" &&
        <div className={classNames(tabContentClassName, styles.content, { [styles.invisibleTab]: !isActiveTab })}>
          {wasActive && children}
        </div>
      }
    </>
  );
});

